import { C8AttributeId, C8NodeType, cnl, getDomainFormInfoForNodeType, IFormField } from "c8-platform";
import { resolveNode, resolveNodesWithDependencies } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import WorkingCache from "csi/state/WorkingCache";
import { routerStore } from "csi/state/rootStore";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { FormDefinitionStore } from "src/SiteAdmin/FormDefinitionConfig/FormDefinitionStore";

export const LEGACY_FORM_DEFINITION_PREFIX = "centric://REFLECTION/INSTANCE/CreateFormDefinition";
const DELIMITER = "/";

/** focus on the first invalid field that has aria-invalid set to true */
export function focusOnFirstInvalidInputField(element: Element): void {
	const inputs = element.querySelectorAll("input:not([type='checkbox']):not([type='radio'])[aria-invalid='true'], textarea[aria-invalid='true']") as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
	inputs[0]?.focus();
}

/** focus on the first input field | TODO: have we considered all input possibilities? */
export function focusOnFirstInputField(element: Element): void {
	const inputs = element.querySelectorAll("input:not([type='checkbox']):not([type='radio']), textarea") as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;
	inputs[0]?.focus();
}

/** Initializes the form definition store by fetching and setting up form data. */
export async function initializeFormDefinitionStore(loc: ILocaleProvider): Promise<FormDefinitionStore> {
	const cache = new WorkingCache();
	const { locationNode: { FormDefinition, RootClassName: formType }, locationUrl } = routerStore;
	await resolveNodesWithDependencies([locationUrl], ["Child:FormDefinition/Child:SetupSettings", "Child:CreateFormDefinition"], cache);
	const formDefinitionConfigNode = cache.get(FormDefinition);
	const setupSettingsNode = cache.get(formDefinitionConfigNode.SetupSettings);
	const isNewFormBuilder = setupSettingsNode.IsNewFormBuilder;
	const formFieldKeyMap = await getDomainFormInfoForNodeType(formType);
	return new FormDefinitionStore(cache, formDefinitionConfigNode, formFieldKeyMap, loc, formType, isNewFormBuilder);
}

/** wrapper component for call construct fields on writer, to create and async submit the writer */
export async function callConstructFieldsOnWriterAndSubmit(nodeURL: cnl, selectedAttributesList: string[]): Promise<void> {
	const writer = new Writer();
	callConstructFieldsOnWriter(writer, nodeURL, selectedAttributesList);
	const res = await submitWriter(writer);
}

/** call construct fields on writer */
export function callConstructFieldsOnWriter(writer: Writer, nodeURL: string, selectedAttributesList: string[]): void {
	writer.callMethod("SiteAdmin", "ConstructFields")
		.addParameter(xml.PROP_URL, xml.ATTR_REF, nodeURL)
		.addParameter("SelectedAttributesList", xml.ATTR_STRINGVECTOR, selectedAttributesList);
}

/** get legacy form field keys by node type, get the latest version of the form definition every time */
export async function getLegacyFormFieldKeysByNodeType(formType: string): Promise<string[] | undefined> {
	const url = `${LEGACY_FORM_DEFINITION_PREFIX}/${formType}`;
	const createFormDefinitionNode = await resolveNode(url);
	if (!createFormDefinitionNode) {
		return;
	}
	const fieldUrls = createFormDefinitionNode.ConfigurableAttributes;
	const paths = createFormDefinitionNode.AttributePaths;
	return createFormFieldKeysFromLegacyFormFieldKeys(formType, fieldUrls, paths);
}

/** create form field keys from the field urls and paths of the legacy form field keys */
export function createFormFieldKeysFromLegacyFormFieldKeys(formType: string, fieldUrls: string[], paths: string[]): string[] {
	return fieldUrls.map((fieldUrl, index) => {
		const splitFieldUrl = fieldUrl.split("/");
		const attributeId = splitFieldUrl[splitFieldUrl.length - 1];
		return createFormFieldKeyFromParts(formType, attributeId, paths[index]);
	});
}

/** get form type from form field key */
export function getFormTypeFromFormFieldKey(formFieldKey: string): string {
	const [formType] = formFieldKey.split(DELIMITER);
	return formType;
}

/** get attribute id from form field key */
export function getAttributeIdFromFormFieldKey(formFieldKey: string): string {
	const [, attributeId] = formFieldKey.split(DELIMITER);
	return attributeId;
}

/** get path from form field key in the form of formType/attributeId/path */
export function getPathFromFormFieldKey(formFieldKey: string): string {
	const [, , path] = formFieldKey.split(DELIMITER);
	return path;
}

/** create form field key */
export function createFormFieldKey(formType: C8NodeType, formField: IFormField): string {
	return createFormFieldKeyFromParts(formType, formField.attributeId, formField.path, formField.id);
}

/** create form field key from attribute id, node type, path, and optional id */
export function createFormFieldKeyFromParts(formType: C8NodeType, attributeId: C8AttributeId, path: string, id?: string): string {
	if (!attributeId) {
		// form only field... return id
		return `${formType}/${id}`;
	}
	const s = `${formType}/${attributeId}${path ? `/${path}` : ""}`;
	return s;
}
